import React, { useEffect, useState } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { can } from "../../../utils/can";
import { useNavigate, useParams } from "react-router-dom";
import { getImageUrl } from "../../../utils/utils";

export default function AddTestimonials() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const API_URL = process.env.REACT_APP_BASE_URL_API;
  const token = localStorage.getItem("adminToken");

  const [activeTab, setActiveTab] = useState("Testimonial");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Edit Mode state (Single item)
  const [editVideoForm, setEditVideoForm] = useState({
    title: "",
    youtubeUrl: "",
    status: true,
  });

  const [editSuccessForm, setEditSuccessForm] = useState({
    title: "",
    file: null,
    viewUrl: "",
    status: true,
  });

  // Multi-Add Mode state (Multiple items)
  const [videoList, setVideoList] = useState([
    { id: 1, title: "", youtubeUrl: "", status: true },
  ]);

  const [storyList, setStoryList] = useState([
    { id: 1, title: "", file: null, status: true },
  ]);

  const [errors, setErrors] = useState({});

  // Helper functions to add/remove rows for Video
  const addVideoRow = () => {
    setVideoList((prev) => [
      ...prev,
      { id: Date.now(), title: "", youtubeUrl: "", status: true },
    ]);
  };

  const removeVideoRow = (indexToRemove) => {
    if (videoList.length <= 1) return;
    setVideoList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const updateVideoRow = (index, field, value) => {
    setVideoList((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  // Helper functions to add/remove rows for Stories
  const addStoryRow = () => {
    setStoryList((prev) => [
      ...prev,
      { id: Date.now(), title: "", file: null, status: true },
    ]);
  };

  const removeStoryRow = (indexToRemove) => {
    if (storyList.length <= 1) return;
    setStoryList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const updateStoryRow = (index, field, value) => {
    setStoryList((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  // Fetch single item for edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await res.json();
        if (!result.success) throw new Error(result.message);

        const data = result.data;
        if (data.formType === "Success") {
          setActiveTab("Success");
          setEditSuccessForm({
            title: data.title || "",
            file: null,
            viewUrl: data.image_video || "",
            status: data.status === "Active",
          });
        } else {
          setActiveTab("Testimonial");
          setEditVideoForm({
            title: data.title || "",
            youtubeUrl: data.image_video || "",
            status: data.status === "Active",
          });
        }
      } catch (err) {
        alert(err.message || "Failed to fetch data");
      }
    };

    fetchData();
  }, [id, isEditMode, API_URL, token]);

  // Submit Video Testimonials
  const handleSubmitVideos = async () => {
    if (can("add_testimonials_and_success_stories", isEditMode ? "edit" : "create") === false) {
      alert("You don't have permission.");
      return;
    }

    const newErrors = {};
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/i;

    if (isEditMode) {
      if (!editVideoForm.title.trim()) newErrors["edit_video_title"] = "Title is required";
      if (!editVideoForm.youtubeUrl.trim()) {
        newErrors["edit_video_url"] = "YouTube URL is required";
      } else if (!youtubeRegex.test(editVideoForm.youtubeUrl.trim())) {
        newErrors["edit_video_url"] = "Please enter a valid YouTube URL";
      }

      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) return;

      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("title", editVideoForm.title.trim());
        formData.append("image_video", editVideoForm.youtubeUrl.trim());
        formData.append("status", editVideoForm.status ? "Active" : "Deactive");
        formData.append("formType", "Testimonial");

        const res = await fetch(`${API_URL}/api/admin/testimonials-list/update/${id}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const result = await res.json();
        if (!result.success) throw new Error(result.message);

        navigate("/testimonials/view-testimonials");
      } catch (err) {
        alert(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    } else {
      // Multi-Add validation
      videoList.forEach((v, idx) => {
        if (!v.title.trim()) newErrors[`video_title_${idx}`] = "Title is required";
        if (!v.youtubeUrl.trim()) {
          newErrors[`video_url_${idx}`] = "YouTube URL is required";
        } else if (!youtubeRegex.test(v.youtubeUrl.trim())) {
          newErrors[`video_url_${idx}`] = "Please enter a valid YouTube URL";
        }
      });

      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) return;

      try {
        setLoading(true);
        const payloadItems = videoList.map((v) => ({
          title: v.title.trim(),
          youtubeUrl: v.youtubeUrl.trim(),
          status: v.status ? "Active" : "Deactive",
          formType: "Testimonial",
        }));

        const formData = new FormData();
        formData.append("items", JSON.stringify(payloadItems));

        const res = await fetch(`${API_URL}/api/admin/testimonials-create`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const result = await res.json();
        if (!result.success) throw new Error(result.message);

        setSuccessMessage(result.message || "Videos added successfully");
        navigate("/testimonials/view-testimonials");
      } catch (err) {
        alert(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
  };

  // Submit Success Stories
  const handleSubmitStories = async () => {
    if (can("add_testimonials_and_success_stories", isEditMode ? "edit" : "create") === false) {
      alert("You don't have permission.");
      return;
    }

    const newErrors = {};

    if (isEditMode) {
      if (!editSuccessForm.title.trim()) newErrors["edit_story_title"] = "Title is required";
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) return;

      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("title", editSuccessForm.title.trim());
        if (editSuccessForm.file) formData.append("image_video", editSuccessForm.file);
        formData.append("status", editSuccessForm.status ? "Active" : "Deactive");
        formData.append("formType", "Success");

        const res = await fetch(`${API_URL}/api/admin/testimonials-list/update/${id}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const result = await res.json();
        if (!result.success) throw new Error(result.message);

        navigate("/testimonials/view-testimonials");
      } catch (err) {
        alert(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    } else {
      // Multi-Add validation
      storyList.forEach((s, idx) => {
        if (!s.title.trim()) newErrors[`story_title_${idx}`] = "Title is required";
        if (!s.file) newErrors[`story_file_${idx}`] = "Image file is required";
      });

      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) return;

      try {
        setLoading(true);
        const payloadItems = storyList.map((s, idx) => ({
          id: idx,
          title: s.title.trim(),
          status: s.status ? "Active" : "Deactive",
          formType: "Success",
        }));

        const formData = new FormData();
        formData.append("items", JSON.stringify(payloadItems));

        storyList.forEach((s, idx) => {
          if (s.file) {
            formData.append(`file_${idx}`, s.file);
          }
        });

        const res = await fetch(`${API_URL}/api/admin/testimonials-create`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const result = await res.json();
        if (!result.success) throw new Error(result.message);

        setSuccessMessage(result.message || "Stories added successfully");
        navigate("/testimonials/view-testimonials");
      } catch (err) {
        alert(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle={isEditMode ? "Edit Testimonials & Success Stories" : "Add Testimonials & Success Stories"} />

        <div className="right_col" role="main">
          <div className="x_panel">
            <div className="x_title">
              <h2>
                {isEditMode ? "Edit Form" : "Entry Form"}
                {successMessage && <span className="text-success ms-3" style={{ fontSize: "14px" }}>{successMessage}</span>}
              </h2>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              <div className="row">
                <div className="col-sm-12">
                  <ul className="nav nav-tabs bar_tabs" id="myTab" role="tablist">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "Testimonial" ? "active" : ""}`}
                        onClick={() => { setActiveTab("Testimonial"); setErrors({}); }}
                        href="#home"
                        role="tab"
                      >
                        Video Testimonials (YouTube)
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "Success" ? "active" : ""}`}
                        onClick={() => { setActiveTab("Success"); setErrors({}); }}
                        href="#profile"
                        role="tab"
                      >
                        Success Stories (Images)
                      </a>
                    </li>
                  </ul>

                  <div className="tab-content" id="myTabContent" style={{ marginTop: "20px" }}>
                    {/* VIDEO TESTIMONIALS TAB */}
                    {activeTab === "Testimonial" && (
                      <div>
                        {isEditMode ? (
                          // Edit Single Video Form
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label font-weight-bold">Testimonial Title <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className="form-control"
                                value={editVideoForm.title}
                                onChange={(e) => setEditVideoForm({ ...editVideoForm, title: e.target.value })}
                                placeholder="Enter title"
                              />
                              {errors.edit_video_title && <p className="text-danger mt-1">{errors.edit_video_title}</p>}
                            </div>

                            <div className="col-md-6 mb-3">
                              <label className="form-label font-weight-bold">
                                YouTube Video Link <span className="text-danger">*</span>
                                {editVideoForm.youtubeUrl && (
                                  <a
                                    href={editVideoForm.youtubeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ms-2 text-primary"
                                    style={{ marginLeft: "10px", fontSize: "12px" }}
                                  >
                                    <i className="fa fa-external-link" /> Test Link
                                  </a>
                                )}
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={editVideoForm.youtubeUrl}
                                onChange={(e) => setEditVideoForm({ ...editVideoForm, youtubeUrl: e.target.value })}
                                placeholder="https://youtu.be/... or https://www.youtube.com/watch?v=..."
                              />
                              {errors.edit_video_url && <p className="text-danger mt-1">{errors.edit_video_url}</p>}
                            </div>

                            <div className="col-12 mb-3">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={editVideoForm.status}
                                  onChange={(e) => setEditVideoForm({ ...editVideoForm, status: e.target.checked })}
                                />
                                <span className="ms-2" style={{ marginLeft: "6px" }}>Active / Deactive</span>
                              </label>
                            </div>
                          </div>
                        ) : (
                          // Multi-Add Video Form
                          <div>
                            {videoList.map((item, idx) => (
                              <div
                                key={item.id}
                                style={{
                                  background: "#f9fbfd",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "10px",
                                  padding: "16px",
                                  marginBottom: "16px",
                                  position: "relative",
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span style={{ fontWeight: "700", color: "#e91e63", fontSize: "14px" }}>
                                    <i className="fa fa-youtube-play" style={{ marginRight: "6px" }} />
                                    Video #{idx + 1}
                                  </span>
                                  {videoList.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeVideoRow(idx)}
                                      title="Remove Video"
                                      style={{ padding: "2px 8px", fontSize: "12px" }}
                                    >
                                      <i className="fa fa-trash" /> Remove
                                    </button>
                                  )}
                                </div>

                                <div className="row">
                                  <div className="col-md-6 mb-2">
                                    <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>
                                      Testimonial Title <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="e.g. Vivashri Success Story - Bihar Couple"
                                      value={item.title}
                                      onChange={(e) => updateVideoRow(idx, "title", e.target.value)}
                                    />
                                    {errors[`video_title_${idx}`] && (
                                      <p className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors[`video_title_${idx}`]}</p>
                                    )}
                                  </div>

                                  <div className="col-md-6 mb-2">
                                    <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>
                                      YouTube Video Link <span className="text-danger">*</span>
                                      {item.youtubeUrl && (
                                        <a
                                          href={item.youtubeUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary"
                                          style={{ marginLeft: "10px", fontSize: "12px" }}
                                        >
                                          <i className="fa fa-external-link" /> Test Link
                                        </a>
                                      )}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="https://youtu.be/... or https://www.youtube.com/watch?v=..."
                                      value={item.youtubeUrl}
                                      onChange={(e) => updateVideoRow(idx, "youtubeUrl", e.target.value)}
                                    />
                                    {errors[`video_url_${idx}`] && (
                                      <p className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors[`video_url_${idx}`]}</p>
                                    )}
                                  </div>

                                  <div className="col-12 mt-1">
                                    <label style={{ fontSize: "13px", cursor: "pointer" }}>
                                      <input
                                        type="checkbox"
                                        checked={item.status}
                                        onChange={(e) => updateVideoRow(idx, "status", e.target.checked)}
                                      />
                                      <span style={{ marginLeft: "6px" }}>Active</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className="mb-4">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={addVideoRow}
                                style={{ borderRadius: "6px", fontWeight: "600" }}
                              >
                                <i className="fa fa-plus-circle" style={{ marginRight: "6px" }} />
                                + Add Another Video
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="text-right mt-3">
                          <button
                            type="button"
                            className="btn sitebtn px-4"
                            disabled={loading}
                            onClick={handleSubmitVideos}
                          >
                            {loading ? "Submitting..." : isEditMode ? "Update Video" : "Submit All Videos"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* SUCCESS STORIES TAB */}
                    {activeTab === "Success" && (
                      <div>
                        {isEditMode ? (
                          // Edit Single Story Form
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label font-weight-bold">Story Title <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className="form-control"
                                value={editSuccessForm.title}
                                onChange={(e) => setEditSuccessForm({ ...editSuccessForm, title: e.target.value })}
                                placeholder="Enter title"
                              />
                              {errors.edit_story_title && <p className="text-danger mt-1">{errors.edit_story_title}</p>}
                            </div>

                            <div className="col-md-6 mb-3">
                              <label className="form-label font-weight-bold">
                                Success Story Image
                                {editSuccessForm.viewUrl && (
                                  <a
                                    href={getImageUrl(editSuccessForm.viewUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ms-2 text-primary"
                                    style={{ marginLeft: "10px", fontSize: "12px" }}
                                  >
                                    <i className="fa fa-external-link" /> View Current Image
                                  </a>
                                )}
                              </label>
                              <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={(e) => setEditSuccessForm({ ...editSuccessForm, file: e.target.files[0] })}
                              />
                              <span className="text-muted d-block mt-1" style={{ fontSize: "12px" }}>
                                Leave empty to keep existing image
                              </span>
                            </div>

                            <div className="col-12 mb-3">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={editSuccessForm.status}
                                  onChange={(e) => setEditSuccessForm({ ...editSuccessForm, status: e.target.checked })}
                                />
                                <span className="ms-2" style={{ marginLeft: "6px" }}>Active / Deactive</span>
                              </label>
                            </div>
                          </div>
                        ) : (
                          // Multi-Add Story Form
                          <div>
                            {storyList.map((item, idx) => (
                              <div
                                key={item.id}
                                style={{
                                  background: "#f9fbfd",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "10px",
                                  padding: "16px",
                                  marginBottom: "16px",
                                  position: "relative",
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span style={{ fontWeight: "700", color: "#DD3481", fontSize: "14px" }}>
                                    <i className="fa fa-image" style={{ marginRight: "6px" }} />
                                    Success Story #{idx + 1}
                                  </span>
                                  {storyList.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeStoryRow(idx)}
                                      title="Remove Story"
                                      style={{ padding: "2px 8px", fontSize: "12px" }}
                                    >
                                      <i className="fa fa-trash" /> Remove
                                    </button>
                                  )}
                                </div>

                                <div className="row">
                                  <div className="col-md-6 mb-2">
                                    <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>
                                      Story Title <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="e.g. Rahul & Priya"
                                      value={item.title}
                                      onChange={(e) => updateStoryRow(idx, "title", e.target.value)}
                                    />
                                    {errors[`story_title_${idx}`] && (
                                      <p className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors[`story_title_${idx}`]}</p>
                                    )}
                                  </div>

                                  <div className="col-md-6 mb-2">
                                    <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>
                                      Story Photo <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="file"
                                      className="form-control"
                                      accept="image/*"
                                      onChange={(e) => updateStoryRow(idx, "file", e.target.files[0])}
                                    />
                                    {errors[`story_file_${idx}`] && (
                                      <p className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors[`story_file_${idx}`]}</p>
                                    )}
                                  </div>

                                  <div className="col-12 mt-1">
                                    <label style={{ fontSize: "13px", cursor: "pointer" }}>
                                      <input
                                        type="checkbox"
                                        checked={item.status}
                                        onChange={(e) => updateStoryRow(idx, "status", e.target.checked)}
                                      />
                                      <span style={{ marginLeft: "6px" }}>Active</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className="mb-4">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={addStoryRow}
                                style={{ borderRadius: "6px", fontWeight: "600" }}
                              >
                                <i className="fa fa-plus-circle" style={{ marginRight: "6px" }} />
                                + Add Another Story
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="text-right mt-3">
                          <button
                            type="button"
                            className="btn sitebtn px-4"
                            disabled={loading}
                            onClick={handleSubmitStories}
                          >
                            {loading ? "Submitting..." : isEditMode ? "Update Story" : "Submit All Stories"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}